import {createContext, useContext} from 'react'
import {AzureModelClient, type PlaygroundMessage, type PlaygroundRequestParameters} from './model-client'
import type {Model, ModelInputSchema, ModelInputSchemaParameter} from '../../types'
import {RetryableError, TokenLimitReachedResponseError} from './playground-types'
import {clearPlaygroundLocalStorage, PlaygroundLocalStorage} from './playground-local-storage'

type PlaygroundState = typeof initialPlaygroundState

export const initialPlaygroundState = {
  messages: [] as PlaygroundMessage[],
  parameters: {} as PlaygroundRequestParameters,
  isLoading: false,
  systemPrompt: '',
  chatInput: '',
  chatClosed: false,
  showParameters: true,
  showParametersOnMobile: false,
  parametersHasChanges: false,
  tab: 'parameters' as 'parameters' | 'details',
  selectedLanguage: 'js',
  iceBreakerCheckPending: true, // used to avoid flashing of empty states before an ice breaker was sent
}

export const PlaygroundStateContext = createContext<PlaygroundState>(initialPlaygroundState)
export const PlaygroundStateDispatcherContext = createContext<React.Dispatch<PlaygroundStateAction>>(
  {} as React.Dispatch<PlaygroundStateAction>,
)
export const PlaygroundManagerContext = createContext<PlaygroundManager>({} as PlaygroundManager)

export function usePlaygroundState() {
  return useContext(PlaygroundStateContext)
}

export function usePlaygroundManager() {
  return useContext(PlaygroundManagerContext)
}

export function usePlaygroundDispatcher() {
  return useContext(PlaygroundStateDispatcherContext)
}

export type PlaygroundStateAction =
  | {type: 'SET_MESSAGES'; messages: PlaygroundMessage[]}
  | {type: 'SET_IS_LOADING'; isLoading: boolean}
  | {type: 'ADD_ERROR_RESPONSE'; message: string}
  | {type: 'SET_PARAMETERS'; parameters: PlaygroundRequestParameters}
  | {type: 'SET_SYSTEM_PROMPT'; systemPrompt: string}
  | {type: 'CLEAR_LAST_PLACEHODER_MESSAGE'}
  | {type: 'RETRY_LAST_MESSAGE'}
  | {type: 'SET_CHAT_INPUT'; chatInput: string}
  | {type: 'SET_CHAT_CLOSED'; chatClosed: boolean}
  | {type: 'SET_SHOW_PARAMETERS'; showParameters: boolean}
  | {type: 'SET_SHOW_PARAMETERS_ON_MOBILE'; showParametersOnMobile: boolean}
  | {type: 'SET_PARAMETERS_HAS_CHANGES'; parametersHasChanges: boolean}
  | {type: 'SET_TAB'; tab: 'parameters' | 'details'}
  | {type: 'SET_SELECTED_LANGUAGE'; selectedLanguage: string}
  | {type: 'SET_ICEBREAKER_CHECK_PENDING'; iceBreakerCheckPending: boolean}

export function tasksReducer(state: PlaygroundState, action: PlaygroundStateAction): PlaygroundState {
  switch (action.type) {
    case 'SET_IS_LOADING':
      return {...state, isLoading: action.isLoading}
    case 'SET_MESSAGES':
      return {...state, messages: action.messages}
    case 'SET_PARAMETERS':
      return {...state, parameters: action.parameters}
    case 'SET_SYSTEM_PROMPT':
      return {...state, systemPrompt: action.systemPrompt}
    case 'ADD_ERROR_RESPONSE':
      return {...state, messages: [...state.messages, {timestamp: new Date(), role: 'error', message: action.message}]}
    case 'CLEAR_LAST_PLACEHODER_MESSAGE': {
      const lastMessage = state.messages.slice(-1)[0]
      if (!lastMessage) return {...state}
      if (lastMessage.role === 'assistant' && lastMessage?.message === '') {
        return {...state, messages: state.messages.slice(0, -1)}
      } else {
        return {...state}
      }
    }
    case 'SET_CHAT_CLOSED': {
      return {...state, chatClosed: action.chatClosed}
    }
    case 'SET_CHAT_INPUT':
      return {...state, chatInput: action.chatInput}
    case 'RETRY_LAST_MESSAGE': {
      const lastUserMessage = [...state.messages].reverse().find(message => message.role === 'user')
      return lastUserMessage ? {...state, chatInput: lastUserMessage.message} : {...state}
    }
    case 'SET_PARAMETERS_HAS_CHANGES':
      return {...state, parametersHasChanges: action.parametersHasChanges}
    case 'SET_SHOW_PARAMETERS':
      return {...state, showParameters: action.showParameters}
    case 'SET_SHOW_PARAMETERS_ON_MOBILE':
      return {...state, showParametersOnMobile: action.showParametersOnMobile}
    case 'SET_TAB':
      return {...state, tab: action.tab}
    case 'SET_SELECTED_LANGUAGE':
      return {...state, selectedLanguage: action.selectedLanguage}
    case 'SET_ICEBREAKER_CHECK_PENDING':
      return {...state, iceBreakerCheckPending: action.iceBreakerCheckPending}
  }
}

export class PlaygroundManager {
  dispatch: React.Dispatch<PlaygroundStateAction>
  client: AzureModelClient
  model: Model
  modelInputSchema: ModelInputSchema
  localStorage = new PlaygroundLocalStorage()

  constructor(
    dispatch: React.Dispatch<PlaygroundStateAction>,
    model: Model,
    playgroundURL: string,
    modelInputSchema: ModelInputSchema,
  ) {
    this.dispatch = dispatch
    this.client = new AzureModelClient(playgroundURL)
    this.model = model
    this.modelInputSchema = modelInputSchema
    this.setPreferedLanguageFromLocalStorage()

    this.setDefaultParameters(modelInputSchema.parameters)
  }

  checkForIcebreakerRan = false
  checkForIcebreaker(message: string | undefined, state: PlaygroundState) {
    if (this.checkForIcebreakerRan) {
      return
    }
    if (state.iceBreakerCheckPending) {
      if (message) {
        this.sendMessage(state, message)
      }
    }
    this.checkForIcebreakerRan = true
    this.dispatch({type: 'SET_ICEBREAKER_CHECK_PENDING', iceBreakerCheckPending: false})
  }

  setPreferedLanguageFromLocalStorage() {
    const savedLanguage = this.localStorage.preferredLanguage
    const language = savedLanguage ?? Object.keys(this.model.samples.code)[0]

    const languages = Object.keys(this.model.samples.code)
    if (language && languages.includes(language)) {
      this.setSelectedLanguage(language)
    }
  }

  setSelectedLanguage(selectedLanguage: string) {
    const languages = Object.keys(this.model.samples.code)
    if (languages.includes(selectedLanguage)) {
      this.localStorage.preferredLanguage = selectedLanguage
      this.dispatch({type: 'SET_SELECTED_LANGUAGE', selectedLanguage})
    }
  }

  handleRequestParameterChange(state: PlaygroundState, key: string, value: string | number | boolean | string[]) {
    const newParams = {
      ...state.parameters,
      [key]: value,
    } as PlaygroundRequestParameters
    this.dispatch({type: 'SET_PARAMETERS', parameters: newParams})
  }

  setDefaultParameters(parameters: ModelInputSchemaParameter[]) {
    const schema = parameters.reduce((acc, parameter) => {
      acc[parameter.key] = parameter.default
      return acc
    }, {} as PlaygroundRequestParameters)
    this.dispatch({type: 'SET_PARAMETERS', parameters: schema})
  }

  setChatInput(chatInput: string) {
    this.dispatch({type: 'SET_CHAT_INPUT', chatInput})
  }

  setSystemPrompt(systemPrompt: string) {
    this.dispatch({type: 'SET_SYSTEM_PROMPT', systemPrompt})
  }

  restoreHistory(setMessages: PlaygroundMessage[]) {
    this.dispatch({type: 'SET_MESSAGES', messages: setMessages})
  }

  setShowParameters(showParameters: boolean) {
    this.dispatch({type: 'SET_SHOW_PARAMETERS', showParameters})
  }

  setShowParametersOnMobile(showParametersOnMobile: boolean) {
    this.dispatch({type: 'SET_SHOW_PARAMETERS_ON_MOBILE', showParametersOnMobile})
  }

  setParametersHasChanges(parametersHasChanges: boolean) {
    this.dispatch({type: 'SET_PARAMETERS_HAS_CHANGES', parametersHasChanges})
  }

  setTab(tab: 'parameters' | 'details') {
    this.dispatch({type: 'SET_TAB', tab})
  }

  resetHistory(clearLocalStorage: boolean) {
    if (clearLocalStorage) {
      clearPlaygroundLocalStorage()
    }
    this.stopStreaming()
    this.dispatch({type: 'SET_MESSAGES', messages: []})
    this.dispatch({type: 'SET_CHAT_CLOSED', chatClosed: false})
  }

  stopStreaming() {
    this.client.streamer?.stop()
  }

  async sendMessage(state: PlaygroundState, message: string): Promise<void> {
    const messages = state.messages

    if (!this.model) {
      return
    }

    messages.push({
      timestamp: new Date(),
      role: 'user',
      message,
    })

    // Add placeholder message
    messages.push({
      timestamp: new Date(),
      role: 'assistant',
      message: '',
    })

    // Update the UI
    this.dispatch({type: 'SET_MESSAGES', messages})
    this.dispatch({type: 'SET_IS_LOADING', isLoading: true})

    // Start work
    try {
      const success = await this.client.sendMessage(
        this.model,
        state.messages,
        state.parameters,
        state.systemPrompt,
        response => {
          this.dispatch({type: 'SET_MESSAGES', messages: [...messages.slice(0, -1), response]})
        },
      )

      if (!success) {
        this.dispatch({type: 'CLEAR_LAST_PLACEHODER_MESSAGE'})
        this.dispatch({type: 'ADD_ERROR_RESPONSE', message: 'An error occurred. Please try again.'})
      }
    } catch (error) {
      // TODO: Messages should probably have an ID so we can refer to them correctly in the future
      this.dispatch({type: 'CLEAR_LAST_PLACEHODER_MESSAGE'})
      if (error instanceof Error) {
        this.dispatch({type: 'ADD_ERROR_RESPONSE', message: error.message})
        if (error instanceof RetryableError) {
          this.dispatch({type: 'RETRY_LAST_MESSAGE'})
        }
        if (error instanceof TokenLimitReachedResponseError) {
          this.dispatch({type: 'SET_CHAT_CLOSED', chatClosed: true})
        }
      } else {
        this.dispatch({type: 'ADD_ERROR_RESPONSE', message: 'An error occurred. Please try again.'})
      }
    } finally {
      this.dispatch({type: 'SET_IS_LOADING', isLoading: false})
    }
  }
}
